import { EmailInput } from "components";
import UserWithAvatar from "components/Atoms/UserWithAvatar";
import CultureLabel from "components/Culture/CultureLabel";
import CulturesTemplateDropdown from "components/dropdowns/reactselect/CulturesTemplateDropdown";
import { InPageEditableSectionField } from "components/MainSectionTemplate/types";
import ChooseTemplateButton from "components/MessageTemplate/ChooseTemplateButton";
import CompanyModalPicker from "components/Picker/CompanyPicker/CompanyModalPicker";
import { ContactParticipant } from "components/Picker/ContactParticipantPicker/ContactParticipantModalPicker";
import UserModalPicker from "components/Picker/UserPicker/UserModalPicker";
import { CompanySmallDetails, ContactSmallDetails } from "components/SmallDetails";
import OptionalTextAreaInput, { OptionalTextAreaDetails } from "components/ValidatedFields/OptionalTextAreaInput";
import OptionalTextInput from "components/ValidatedFields/OptionalTextInput";
import ParticipantDebugInfo from "eSign/components/Participant/ParticipantDebugInfo";
import { SelectSenderSignerModal } from "eSign/components/Participant/SelectSenderSignerModal";
import { SignNow } from "eSign/components/SignNowButtonWithModal";
import React from "react";
import { isNullOrEmptyString } from "utilities";
import { messageTemplateForESignSigner } from "utilities/documentUtils";
import { getCultureFromParticipant, getLcidFromParticipant } from "utilities/esigningUtils";

export interface CommonSignatoryFieldProps {
  esignDocument: server.dto.ESignDocument
  participant: server.dto.ESignParticipant;
  role: server.dto.ESignParticipantRoles;
  hasParticipant: boolean;
  editDisabled: boolean;
  progress: server.dto.ESignParticipantProgress;
  contact: server.dto.ContactSmallV2;
}

const updateContactInstruction = (props: CommonSignatoryFieldProps, partial: Partial<server.dto.SetESignParticipantFromContactInstruction>): server.dto.SetESignParticipantFromContactInstruction => ({
  documentId: props.esignDocument.documentId,
  role: props.role,
  companyId: props.participant?.company?.companyId,
  contactId: props.contact?.contactId,
  contactCulture: getLcidFromParticipant(props.participant?.linkedToUser, props.contact),
  emailAddress: props.participant?.email,
  invitationSubject: props.participant?.invitationSubject,
  invitationBody: props.participant?.invitationBody,
  ...partial,
});

const updateUserInstruction = (props: CommonSignatoryFieldProps, partial: Partial<server.dto.SetESignSenderFromUserInstruction>): server.dto.SetESignSenderFromUserInstruction => ({
  documentId: props.esignDocument.documentId,
  companyId: props.participant?.company?.companyId,
  userId: props.participant?.linkedToUser?.userId,
  ...partial,
})

const getKey = (props: CommonSignatoryFieldProps, key: string): string => `${props.role}_${key}`;

export const UserField = (props: CommonSignatoryFieldProps): InPageEditableSectionField => ({
  key: getKey(props, "user"),
  label: RESX.SoUser.User,
  isEmpty: props.participant?.linkedToUser == null,
  render: () => <UserWithAvatar user={props.participant.linkedToUser} />,
  inPageEdit: {
    render: ({ onChange }) => <div className="main-section-template-fields-padding">
      <UserModalPicker
        startOpen
        value={null}
        onChange={v => onChange(v, true)}
        validatorKey="participantUser"
        required={false}
        canCreateNew={false}
      />
    </div>,
    fullWidth: false,
    type: "custom",
    initialValue: props.participant?.linkedToUser,
    submitChange: (value: server.dto.UserSmall) => {
      const instr = updateUserInstruction(props, { userId: value.userId });
      return window.CoreApi.ESign.SetSenderFromUser(instr)
    }
  }
});

interface ContactFieldProps extends CommonSignatoryFieldProps {
  hasError: boolean;
  senderSigner: "sender" | "signer";
}

export const ContactField = (props: ContactFieldProps): InPageEditableSectionField => ({
  key: getKey(props, "contact"),
  label: RESX.Contact.resxContact,
  isEmpty: !props.hasParticipant,
  error: props.hasError,
  render: () => <ContactSmallDetails
    contact={props.contact}
  />,
  inPageEdit: {
    render: ({ onChange, cancel, error }) => <div className="main-section-template-fields-padding">
      <SelectSenderSignerModal
        startOpen={!error}
        documentType={props.esignDocument.documentType}
        documentId={props.esignDocument.documentId}
        onClose={cancel}
        onChange={(participant) => onChange(participant, true)}
        hasError={props.hasError}
        senderOrSigner={props.senderSigner}
      />
    </div>,
    fullWidth: false,
    type: "custom",
    initialValue: props.contact,
    submitChange: (value: ContactParticipant) => {
      const instr = updateContactInstruction(props, {
        contactId: value.contact.contactId,
        emailAddress: value.email,
        contactCulture: value.culture.lcid,
        companyId: value.company?.companyId,
        invitationBody: value.invitationBody,
        invitationSubject: value.invitationSubject
      });
      return window.CoreApi.ESign.SetParticipantFromContact(instr);
      //return props.setContact(instr);
    }
  }
});

interface RepresentingFieldProps extends CommonSignatoryFieldProps {
  onCompanyChange: () => void;
}

export const RepresentingField = (props: RepresentingFieldProps): InPageEditableSectionField => ({

  key: getKey(props, "representing"),
  label: RESX.ESign.Representing,
  isEmpty: props.participant?.company == null,
  render: () => <CompanySmallDetails
    company={props.participant.company}
    layout="multiLine"
    onChange={props.onCompanyChange}
  />,
  inPageEdit: {
    render: ({ onChange }) => <div className="main-section-template-fields-padding">
      <CompanyModalPicker
        startOpen
        value={null}
        onChange={c => onChange(c, true)}
        modalTitle={RESX.Company.resxCompany}
        addressType="administrative"
        canCreateNew={false}
        validatorKey="participantCompany"
        required={false}
      />
    </div>,
    fullWidth: false,
    type: "custom",
    initialValue: props.participant?.company,
    submitChange: (value: server.dto.CompanySmall) => {
      if (props.participant.linkedToUser) {
        const instr = updateUserInstruction(props, { companyId: value.companyId });
        return window.CoreApi.ESign.SetSenderFromUser(instr)
      } else {
        const instr = updateContactInstruction(props, { companyId: value.companyId });
        return window.CoreApi.ESign.SetParticipantFromContact(instr)
      }
    }
  }
});

export const EmailField = (props: CommonSignatoryFieldProps): InPageEditableSectionField => ({
  key: getKey(props, "email"),
  label: RESX.GeneralLabels.Email,
  isEmpty: isNullOrEmptyString(props.participant?.email),
  render: () => props.participant.email,
  inPageEdit: {
    render: ({ value, onChange, cancel, submit }) => <EmailInput
      focusOnMount
      value={value}
      onChange={onChange}
      validatorKey="participantEmail"
      onEnter={submit}
      onEsc={cancel}
      emailCanBeBlank={false}
    />,
    fullWidth: true,
    type: "confirmOrCancelButton",
    initialValue: props.participant?.email,
    submitChange: (value: string) => {
      const instr = updateContactInstruction(props, { emailAddress: value });
      return window.CoreApi.ESign.SetParticipantFromContact(instr)
    }
  }
})

export const LanguageField = (props: CommonSignatoryFieldProps): InPageEditableSectionField => ({
  key: getKey(props, "language"),
  label: RESX.Language.resxLanguage,
  render: () => <CultureLabel culture={getCultureFromParticipant(props.participant?.linkedToUser, props.contact)} />,
  inPageEdit: {
    initialValue: getLcidFromParticipant(props.participant?.linkedToUser, props.contact),
    fullWidth: true,
    render: ({ value, onChange }) => (
      <CulturesTemplateDropdown
        value={value}
        onChange={c => onChange(c, true)}
        config={{
          focusOnMount: true,
          openMenuOnFocus: true
        }}
      />
    ),
    type: "confirmOrCancelButton",
    submitChange: (value: server.dto.LCID) => {
      const instruction = updateContactInstruction(props, { contactCulture: value });
      return window.CoreApi.ESign.SetParticipantFromContact(instruction);
    }
  }
});

export const TemplateSubjectField = (props: CommonSignatoryFieldProps & { display: boolean; }): InPageEditableSectionField => ({
  key: getKey(props, "templateSubject"),
  label: RESX.Message.Subject,
  isEmpty: isNullOrEmptyString(props.participant?.invitationSubject),
  hidden: !props.display,
  render: () => props.participant.invitationSubject,
  inPageEdit: {
    render: ({ value, onChange, cancel, submit }) => <OptionalTextInput
      focusOnMount
      value={value}
      onChange={onChange}
      onEnter={submit}
      onEsc={cancel}
      placeholder={RESX.Message.Subject}
      maxLength={serverReference.constants.messageTemplate.maxTranslationSubjectLength}
    />,
    fullWidth: true,
    type: "confirmOrCancelButton",
    initialValue: props.participant?.invitationSubject,
    submitChange: (value: string) => {
      const instr = updateContactInstruction(props, { invitationSubject: value });
      return window.CoreApi.ESign.SetParticipantFromContact(instr)
    }
  }
});

export const TemplateBodyField = (props: CommonSignatoryFieldProps & { display: boolean }): InPageEditableSectionField => ({
  key: getKey(props, "templateBody"),
  label: "Body",
  isEmpty: isNullOrEmptyString(props.participant?.invitationBody),
  render: () => <OptionalTextAreaDetails>{props.participant.invitationBody}</OptionalTextAreaDetails>,
  hidden: !props.display,
  inPageEdit: {
    render: ({ value, onChange, cancel, submit }) => <OptionalTextAreaInput
      focusOnMount
      placeholder={RESX.Invitation.WriteAMessageThatWillBeDisplayed}
      rows={8}
      value={value}
      maxLength={serverReference.constants.messageTemplate.maxTranslationBodyLength}
      onCtrlEnter={submit}
      onEsc={cancel}
      onChange={onChange}
    />,
    fullWidth: true,
    type: "confirmOrCancelButton",
    initialValue: props.participant?.invitationBody,
    submitChange: (value: string) => {
      const instr = updateContactInstruction(props, { invitationBody: value });
      return window.CoreApi.ESign.SetParticipantFromContact(instr)
    }
  }
});

export const InvitationTitleField = (props: CommonSignatoryFieldProps & { display: boolean, onTemplateSelected: (template: server.dto.MessageTemplate, language: server.dto.Languages) => void }): InPageEditableSectionField => ({
  key: getKey(props, "title"),
  label: "",
  hideLabel: true,
  hidden: !props.display,
  render: () => <div className="flex justify-space-between">
    <h3>{RESX.Invitation.resxInvitation}</h3>

    {!props.editDisabled && <div>
      <ChooseTemplateButton
        type={messageTemplateForESignSigner(props.esignDocument.documentType)}
        buttonLabel={RESX.InvitationTemplate.resxInvitationTemplate}
        onTemplateSelected={props.onTemplateSelected}
      />
    </div>}
  </div>,
})

export const DebugField = (props: CommonSignatoryFieldProps): InPageEditableSectionField => ({
  key: getKey(props, "debug"),
  label: "Debug",
  hidden: !props.hasParticipant || (!site.debug && !site.isImpersonated),
  render: () => <ParticipantDebugInfo participant={props.participant} />,
})

export const SignNowField = (props: CommonSignatoryFieldProps): InPageEditableSectionField => ({
  key: getKey(props, "signNow"),
  hidden: (props.progress.value !== "readyForSigning") || (props.participant.linkedToUser?.userId !== site.currentUser.userId),
  label: "",
  hideLabel: true,
  render: () => <div className="flex justify-end">
    <SignNow participant={props.participant} />
  </div>
});
